import { useContext, useMemo, useRef, useState } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';

import Labels from '@pages/Onboarding/components/Labels/Labels';
import { QuickActionMenu } from '@pages/Onboarding/components/QuickActionMenu/QuickActionMenu';

import { IOnboardingGroup } from '@models/OnboardingGroup.model';

import {
  openErrorDialog,
  openWarningDialog,
} from '@services/mixpanel/trackers/Onboarding';
import { NavigationContext } from '@layout/navigation/navigation';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { patchApproximateMatchByProductId } from '@services/queries/onboarding/patchApproximateMatchByProductId';
import {
  FieldErrorTypes,
  OnboardingFileContext,
} from '../../OnboardingFile.query';
import {
  Line,
  LineText,
  NoText,
  Item,
  ErrorContainer,
  LineTextError,
  LineContainer,
  RightContainer,
  TextStatusContainer,
  TextStatus,
  FirstColumn,
  RightIcon,
  Header,
  ColumnsFieldsWrapper,
  ColumnsFields,
  ColumnField,
  AssetRow,
  Assets,
  AssetField,
  AssetFieldContainer,
  TipTitle,
  ToolTipText,
  OuterItem,
  ConfirmMatchActionItem,
  Label,
  RightColumn,
  RejectMatchAction,
} from './GroupRow.styles';
import { MatchedItemWrapper } from '../MatchedItemWrapper/MatchedItemWrapper';

interface IFillOrEmpty {
  group: IOnboardingGroup;
  label: string;
  field: FieldErrorTypes;
  isError?: boolean;
  textError?: string;
  textStatus?: {
    text: string;
    progress: number;
  };
}

const FillOrEmpty = ({
  group,
  label,
  isError,
  textError,
  field,
  textStatus,
}: IFillOrEmpty) => {
  const {
    errorDialog,
    selectedGroup,
    fieldErrorSelected,
    errorManufacturerDialog,
  } = useContext(OnboardingFileContext);
  const { studioName, isSuperAdmin } = useContext(NavigationContext);

  const params = useParams<{ id: string }>();

  if (isError) {
    return (
      <ErrorContainer>
        <LineTextError limitOfLines={1}>{label || '-'}</LineTextError>
        <Labels
          isBorder={isSuperAdmin}
          type="error"
          text={textError || ''}
          onClick={e => {
            if (isSuperAdmin) e.stopPropagation();
            selectedGroup.set(group);
            fieldErrorSelected.set(field);

            openErrorDialog(studioName, params.id, group.id, field);

            if (field === 'manufacturer') {
              errorManufacturerDialog.set(true);
            } else {
              errorDialog.set(true);
            }
          }}
        />
      </ErrorContainer>
    );
  }

  if (!label) {
    return <NoText>Empty</NoText>;
  }

  if (textStatus?.text === 'updating') {
    return (
      <TextStatusContainer>
        <TextStatus limitOfLines={1}>{label}</TextStatus>

        <Labels
          isBorder={isSuperAdmin}
          text={textStatus.text}
          toolTipId="modelUpdateTooltip"
          type="loading"
          toolTipContent={
            <>
              <TipTitle>Verifying Changes</TipTitle>
              <ToolTipText>
                If we identify the product, and there are no more errors, this
                item will move to the “Identified Products” tab. Otherwise,
                you’ll see the new error here.
              </ToolTipText>
            </>
          }
          loadingProgress={textStatus.progress}
        />
      </TextStatusContainer>
    );
  }

  return (
    <LineContainer>
      <LineText limitOfLines={1}>{label} </LineText>
    </LineContainer>
  );
};

const FillOrEmptyAssetField = ({ label }: { label: string | number }) => {
  if (label === null || !label) return <NoText>Empty</NoText>;

  return <AssetField>{label}</AssetField>;
};

interface IGroupRow {
  group: IOnboardingGroup;
}

const GroupRow = ({ group }: IGroupRow) => {
  const { status, selectedGroup, accessoryDialog } = useContext(
    OnboardingFileContext,
  );
  const { studioName, isSuperAdmin, studioId } = useContext(NavigationContext);

  const params = useParams<{ id: string }>();

  const [openGroup, setOpenGroup] = useState<boolean>(false);
  const [matchAction, setMatchAction] = useState<string>('');
  const columnsFieldRef = useRef<null | HTMLDivElement>(null);
  const assetFieldRef = useRef<null | HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const getTextError = (textError: string | null) => {
    if (textError?.includes('Invalid')) {
      return 'Invalid';
    }
    if (textError?.includes('Not Found')) {
      return 'Not Found';
    }

    return 'Empty';
  };

  const isAccessory =
    !group.isNotAccessory &&
    group.accessoryError &&
    [
      'identification_requested',
      'unidentified',
      'approx_match',
      'inventory',
    ].includes(status);

  const patchApproximateMatchMutation = useMutation(
    patchApproximateMatchByProductId,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['onboarding', 'files']);
      },
    },
  );

  const columns = useMemo(() => {
    return [
      ...Object.keys(group.items[0].equipmentFields || []),
      ...Object.keys(group.items[0].otherFields || []),
    ];
  }, []);

  return (
    <OuterItem data-isfindmatched={status}>
      {status === 'approx_match' && (
        <MatchedItemWrapper
          title="Most Confident Potential Match:"
          fields={[
            {
              text: group.approxMatch[0].mfr,
              image: group.approxMatch[0].image,
            },
            { text: group.approxMatch[0].mpn },
            { text: group.approxMatch[0].productName },
          ]}
        >
          <RightColumn>
            <RejectMatchAction
              disabled={
                patchApproximateMatchMutation.isLoading &&
                matchAction === 'rejectMatch'
              }
              onClick={() => {
                setMatchAction('rejectMatch');
                patchApproximateMatchMutation.mutate({
                  groupId: group.id,
                  studioId,
                  fileId: params.id,
                  confirm: false,
                });
              }}
            >
              Not Correct
            </RejectMatchAction>
            <ConfirmMatchActionItem
              disabled={
                patchApproximateMatchMutation.isLoading &&
                matchAction === 'acceptMatch'
              }
              onClick={() => {
                setMatchAction('acceptMatch');
                patchApproximateMatchMutation.mutate({
                  groupId: group.id,
                  studioId,
                  fileId: params.id,
                  confirm: true,
                  approxMatch: {
                    image: group.approxMatch[0].image,
                    mpn: group.approxMatch[0].mpn,
                    mfr: group.approxMatch[0].mfr,
                    productId: group.approxMatch[0].productId,
                    productName: group.approxMatch[0].productName,
                  },
                });
              }}
            >
              Confirm Match
            </ConfirmMatchActionItem>
          </RightColumn>
        </MatchedItemWrapper>
      )}
      {status === 'approx_match' && <Label>Your Details:</Label>}
      <Item data-status={status} data-isfindmatched={status}>
        <Header
          isAccessory={!!isAccessory}
          data-isgroupopen={!!openGroup}
          data-status={status}
          data-isfindmatched={status}
        >
          <Line
            onClick={() => {
              setOpenGroup(!openGroup);
            }}
          >
            <FirstColumn>
              <RightIcon>
                <BlueIcon
                  icon={openGroup ? 'chevron-down' : 'chevron-right'}
                  color="#8F95B2"
                />
              </RightIcon>
              <FillOrEmpty
                field="manufacturer"
                group={group}
                label={group.manufacturer}
                isError={
                  !!group.mfrError &&
                  [
                    'unidentified',
                    'identification_requested',
                    'approx_match',
                  ].includes(status) &&
                  !isAccessory
                }
                textError={getTextError(group.mfrError)}
              />
            </FirstColumn>
            <FillOrEmpty
              field="model"
              group={group}
              label={group.model}
              isError={
                !!group.mpnError &&
                [
                  'unidentified',
                  'identification_requested',
                  'approx_match',
                ].includes(status) &&
                !isAccessory
              }
              textError={getTextError(group.mpnError)}
            />
            <FillOrEmpty
              field="productName"
              group={group}
              label={group.productName}
              isError={
                !!group.productNameError &&
                [
                  'unidentified',
                  'identification_requested',
                  'approx_match',
                ].includes(status) &&
                !isAccessory
              }
              textError={getTextError(group.productNameError)}
            />
            <LineContainer>
              <LineText limitOfLines={1}>{group.count}</LineText>
            </LineContainer>
            <RightContainer>
              {isAccessory ? (
                <Labels
                  isBorder={isSuperAdmin}
                  text="Possible Accessory"
                  type="warning"
                  onClick={e => {
                    isSuperAdmin && e.stopPropagation();
                    accessoryDialog.set(true);
                    selectedGroup.set(group);
                    openWarningDialog(studioName, params.id, group.id);
                  }}
                />
              ) : (
                <QuickActionMenu group={group} status={status} />
              )}
            </RightContainer>
          </Line>
        </Header>

        {openGroup && (
          <ColumnsFieldsWrapper>
            <ColumnsFields
              isAccessory={!!isAccessory}
              columns={columns.length}
              ref={columnsFieldRef}
              data-status={status}
              data-isfindmatched={status}
            >
              {columns.map(key => (
                <ColumnField key={key}>{key}</ColumnField>
              ))}
            </ColumnsFields>
          </ColumnsFieldsWrapper>
        )}
        {openGroup && (
          <Assets
            ref={assetFieldRef}
            onScroll={() => {
              if (columnsFieldRef.current && assetFieldRef.current)
                columnsFieldRef.current.scrollLeft =
                  assetFieldRef.current.scrollLeft;
            }}
          >
            {group.items.map(asset => (
              <AssetRow key={asset.id} columns={columns.length}>
                {asset.equipmentFields &&
                  Object.keys(asset.equipmentFields).map(equipmentKey => (
                    <AssetFieldContainer key={equipmentKey}>
                      <FillOrEmptyAssetField
                        label={asset.equipmentFields[equipmentKey]}
                      />
                    </AssetFieldContainer>
                  ))}
                {asset.otherFields &&
                  Object.keys(asset.otherFields).map(otherKey => (
                    <AssetFieldContainer key={otherKey}>
                      <FillOrEmptyAssetField
                        label={asset.otherFields[otherKey]}
                      />
                    </AssetFieldContainer>
                  ))}
              </AssetRow>
            ))}
          </Assets>
        )}
      </Item>
    </OuterItem>
  );
};

export { GroupRow };
